import { useApolloClient } from "@apollo/client";
import { CouponTypeEnum, ProductTypeEnum } from "API";
import Currency from "currency.js";
import gql from "graphql-tag";
import { validateCoupon } from "graphql/queries";
import { withApollo } from "helper/apollo";
import { addGtmEvent, getAddToCart } from "helper/googleTags";
import { getLocalState, setLocalState } from "helper/localStorage";
import { CartProduct, Coupon } from "interfaces";
import moment from "moment-timezone";
import React, { useEffect, useReducer, useState } from "react";

import { CartContext } from "./cart.context";

const initialState = {
  isOpen: false,
};

function reducer(cartState: any, action: any) {
  switch (action.type) {
    case "TOGGLE":
      return {
        ...cartState,
        isOpen: !cartState.isOpen,
      };
    default:
      return cartState;
  }
}

export type Cart = {
  orderStartTime: string;
  orderEndTime: string;
  tripDays: number;
  tripDiscountMultiplier: number;
  products: CartProduct[];
  coupons: Coupon[];
  total: number;
  subTotal: number;
  discountTotal: number;
  couponDiscountTotal: number;
  tripDiscountTotal: number;
  depositTotal: number;
  region: string;
  stockLocation: string;
  fulfillmentCenter: string;
};

function getInitialCart(): Cart {
  return {
    orderStartTime: null,
    orderEndTime: null,
    tripDays: 0,
    tripDiscountMultiplier: 1,
    products: [],
    total: 0,
    subTotal: 0,
    depositTotal: 0,
    discountTotal: 0,
    couponDiscountTotal: 0,
    tripDiscountTotal: 0,
    coupons: [],
    region: null,
    stockLocation: null,
    fulfillmentCenter: null,
  };
}

export interface LineTotal {
  total: number;
  subTotal: number;
  discountTotal: number;
  depositTotal: number;
}

function getTripDiscount(tripDays) {
  if (tripDays >= 4 && tripDays <= 7) {
    return 0.9;
  }
  if (tripDays >= 8 && tripDays <= 14) {
    return 0.8;
  }
  if (tripDays >= 15 && tripDays <= 20) {
    return 0.7;
  }
  if (tripDays >= 21 && tripDays <= 30) {
    return 0.65;
  }
  if (tripDays >= 31 && tripDays <= 40) {
    return 0.6;
  } else return 1;
}

const log = (...content) => {
  if (process.env.NODE_ENV !== "production") console.log(...content);
};
const CartProvider = (props: any) => {
  const [cartState, dispatch] = useReducer(reducer, initialState);
  const [cart, setCart] = useState(
    getLocalState("bbcart") || (getInitialCart() as Cart)
  );
  const client = useApolloClient();

  useEffect(() => {
    setLocalState("bbcart", cart);
    if (process.env.NODE_ENV !== "production") console.log("cart", cart);
    if (cart && cart.orderStartTime) {
      if (
        moment
          .tz(cart.orderStartTime, "Africa/Johannesburg")
          .isBefore(moment.tz("Africa/Johannesburg"), "day")
      ) {
        setCart((cart) => ({
          ...cart,
          orderStartTime: null,
          orderEndTime: null,
        }));
      }
    }
  }, [cart]);

  useEffect(() => {
    calculateCart(cart);
  }, [cart.tripDiscountMultiplier, cart.tripDays, cart.region]);

  function toggle() {
    dispatch({ type: "TOGGLE" });
  }

  function getProduct(id) {
    return cart.products.find((p) => p.id === id);
  }

  function getProductPrice(tripDays, price, type = null) {
    if (type === ProductTypeEnum.RETAIL_ITEM) {
      return price;
    }
    const tripDiscount = getTripDiscount(tripDays);
    return Number((price * tripDiscount).toFixed(2));
  }

  function getProductTripTotal(product, qty = 1): LineTotal {
    return calculateLineTotal(cart, product, qty);
  }

  function setShippingOption(product, qty = 1): LineTotal {
    return calculateLineTotal(cart, product, qty);
  }

  function setRegion(region: string): void {
    function getName(locationId) {
      if (locationId === "cpt") return "Cape Town";
    }

    function mapRegion(region) {
      switch (region) {
        case "Eastern Cape":
        case "Western Cape":
        case "Northern Cape":
        case "Gauteng":
        case "Gauteng North":
        case "Gauteng South":
        case "Free State":
        case "North West":
        case "Limpopo":
        case "KwaZulu-Natal":
        case "Mpumalanga":
          return "cpt";
        default:
          return "cpt";
      }
    }

    setCart((cart) => {
      const stockLocation = mapRegion(region);
      if (cart.stockLocation !== stockLocation) {
        cart.products = [];
      }

      const newCart = {
        ...cart,
        region: region,
        stockLocation,
        fulfillmentCenter: getName(stockLocation),
      };
      return calculateCart(newCart);
    });
  }

  const calculateLineTotal = (cart, product, qty = 1): LineTotal => {
    // total = subtotal + discountTotal
    const totals = {
      total: 0,
      subTotal: 0,
      discountTotal: 0,
      depositTotal: product?.deposit * qty || 0,
    };

    let daysMultiple = cart.tripDays;
    if (product.type === ProductTypeEnum.RETAIL_ITEM) {
      // this is a retail item so dont calculate the price per day, only
      // the quantity price
      daysMultiple = 1;
    }

    /*
     * Calculate the total without any discounts - i.e. (price * days) - then calculate the total with possible
     * discount, and compare the values to get the actual discount applied per line.
     */
    const noDiscountValue = Number(
      Currency(qty).multiply(product.price).multiply(daysMultiple).value
    );
    totals.subTotal = noDiscountValue;
    totals.total = Number(
      Currency(qty)
        .multiply(getProductPrice(daysMultiple, product.price, product.type))
        .multiply(daysMultiple).value
    );
    totals.discountTotal = totals.subTotal - totals.total;
    return totals;
  };
  /*
   * Calculate the price of the items for the duration of the trip
   */
  const calculateCart = (cart: Cart): Cart => {
    const newProducts = [];
    const { products } = cart;
    const totals = {
      total: 0,
      subTotal: 0,
      tripDiscountTotal: 0,
      couponDiscountTotal: 0,
      discountTotal: 0,
      depositTotal: 0,
    };
    for (let product of products) {
      const { total, subTotal, discountTotal, depositTotal } =
        calculateLineTotal(cart, product, product.qty);
      totals.total += total;
      totals.subTotal += subTotal;
      totals.tripDiscountTotal += discountTotal;
      totals.depositTotal += depositTotal;
      newProducts.push({
        ...product,
        total,
        subTotal,
        discountTotal,
        depositTotal,
      });
    }

    if (cart.coupons.length > 0) {
      /*
       * Calculate the coupon discounts
       * Only calculate on RENTAL items, don't include RETAIL items
       */
      const rentalTotal = cart.products.reduce(
        (acc, curr) =>
          curr.type !== ProductTypeEnum.RETAIL_ITEM
            ? acc + getProductTripTotal(curr, curr.qty).total
            : 0,
        0
      );

      const discount = cart.coupons.reduce(
        (acc, coupon) => {
          let discount = 0;
          if (coupon.type === CouponTypeEnum.PERCENTAGE) {
            discount = (coupon.value / 100) * acc.netTotal;
          }
          if (coupon.type === CouponTypeEnum.FIXED) {
            discount = coupon.value;
          }

          const newNetTotal = acc.netTotal - discount;
          return {
            netTotal: Math.max(newNetTotal, 0),
            discountTotal:
              newNetTotal < 0 ? acc.netTotal : acc.discountTotal + discount,
          };
        },
        {
          netTotal: rentalTotal,
          discountTotal: 0,
        }
      );

      totals.couponDiscountTotal = discount.discountTotal;
    }

    /*
     * Set the basket totals
     * IMPORTANT!!!!!
     * The total already includes the tripDiscountTotal because the totals for every product is displayed with the
     * discount already applied.....maybe not ideal but we'll see....
     *
     * So only use the couponDiscountTotal to calculate the total (as it already includes the tripDiscountTotal)
     * but still use both to calculate discountTotal as it represents the total discount applied
     */
    totals.discountTotal =
      totals.couponDiscountTotal + totals.tripDiscountTotal;
    totals.total = Math.max(totals.subTotal - totals.discountTotal, 0);

    return {
      ...cart,
      products: newProducts,
      ...totals,
    };
  };

  const add = (item: CartProduct) => {
    setCart((cart) => {
      if (cart.products.length > 0) {
        const index = cart.products.findIndex(
          (product) => product.id === item.id
        );
        if (index !== -1) {
          // if product already available in the cart
          const product: CartProduct = cart.products[index];
          const qty = (product.qty || 0) + 1;
          const totals = calculateLineTotal(cart, item, qty);

          cart.products[index] = {
            ...product,
            ...item,
            qty,
            ...totals,
          }; // just increase the quantity
        } else {
          // if this product is not available in the cart
          const qty = 1;
          const totals = calculateLineTotal(cart, item, 1);

          cart.products.push({
            ...item,
            qty,
            ...totals,
          });
        }
      } else {
        // if the cart is empty
        const qty = 1;
        const totals = calculateLineTotal(cart, item, 1);
        cart.products.push({
          ...item,
          qty,
          ...totals,
        });
      }
      return calculateCart(cart);
    });
    addGtmEvent(
      getAddToCart({
        name: item.name,
        id: item.id,
        price: item.price,
        category: "",
        qty: item.qty ?? 1,
        tripDays: cart.tripDays,
      })
    );
  };

  async function addCoupon(input) {
    return client
      .query({
        query: gql(validateCoupon),
        variables: {
          input,
        },
      })
      .then(({ data: { validateCoupon } }) => {
        setCart((cart) => {
          cart.coupons.push(validateCoupon);
          return calculateCart(cart);
        });
      });
  }

  const removeCoupon = (id) => {
    setCart((cart) => {
      cart.coupons = cart.coupons.filter((c) => c.id !== id);
      return calculateCart(cart);
    });
  };

  const update = (itemId: any, qty: number) => {
    setCart((cart) => {
      const { products } = cart;
      const index = products.findIndex((product) => product.id === itemId);
      if (qty < 1 && index > -1) {
        // delete if quantity, 0
        products.splice(index, 1);
      } else {
        // update quantity
        const product = products[index];
        const totals = calculateLineTotal(cart, product, qty);

        products[index] = {
          ...product,
          ...totals,
          qty,
        };
      }

      return calculateCart(cart);
    });
  };

  const clearCart = () => {
    setCart(getInitialCart());
  };
  const setOrderStartTime = (date: string) => {
    setCart((cart) => {
      cart.orderStartTime = moment
        .tz(date, "Africa/Johannesburg")
        .startOf("day")
        .toISOString();
      if (!cart.orderEndTime) {
        cart.orderEndTime = moment
          .tz(date, "Africa/Johannesburg")
          .endOf("day")
          .toISOString();
      } else if (
        moment
          .tz(cart.orderEndTime, "Africa/Johannesburg")
          .isBefore(moment.tz(cart.orderStartTime, "Africa/Johannesburg"))
      ) {
        cart.orderEndTime = moment
          .tz(date, "Africa/Johannesburg")
          .endOf("day")
          .toISOString();
      } else if (cart.orderEndTime) {
        cart.tripDays =
          moment
            .tz(cart.orderEndTime, "Africa/Johannesburg")
            .diff(
              moment.tz(cart.orderStartTime, "Africa/Johannesburg"),
              "days"
            ) + 1;
        cart.tripDiscountMultiplier = getTripDiscount(cart.tripDays);
      }
      return calculateCart(cart);
    });
  };
  const setOrderEndTime = (date: string) => {
    setCart((cart) => {
      cart.orderEndTime = moment
        .tz(date, "Africa/Johannesburg")
        .endOf("day")
        .toISOString();
      if (
        moment
          .tz(cart.orderEndTime, "Africa/Johannesburg")
          .isBefore(moment.tz(cart.orderStartTime, "Africa/Johannesburg"))
      ) {
        cart.orderStartTime = moment
          .tz(date, "Africa/Johannesburg")
          .startOf("day")
          .toISOString();
      }
      cart.tripDays =
        moment
          .tz(cart.orderEndTime, "Africa/Johannesburg")
          .diff(moment.tz(cart.orderStartTime, "Africa/Johannesburg"), "days") +
        1;
      cart.tripDiscountMultiplier = getTripDiscount(cart.tripDays);
      log(
        `updating orderEndTime`,
        cart.tripDays,
        cart.orderStartTime,
        cart.orderEndTime
      );
      return calculateCart(cart);
    });
  };

  return (
    <CartContext.Provider
      value={{
        cartState,
        dispatch,
        toggle,
        cart,
        add,
        setRegion,
        getProductPrice,
        getProduct,
        update,
        addCoupon,
        removeCoupon,
        setOrderStartTime,
        setOrderEndTime,
        clearCart,
        getProductTripTotal,
      }}
    >
      {props.children}
    </CartContext.Provider>
  );
};
export default withApollo(CartProvider);
